<template>
  <div class="card mb-4 border-0 shadow-sm">

    <!-- UPDATED: Improved flex behavior for mobile to prevent overlapping -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center bg-white border-radius-xl gap-2">
      <h6 class="text-dark fw-bold mb-0">{{ $t('users') || 'Users' }}</h6>
      
      <!-- UPDATED: Added flex-wrap and gap to handle narrow screens -->
      <div class="d-flex flex-wrap align-items-center w-100 w-md-auto justify-content-start justify-content-md-end gap-2">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm shadow-none">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-violet text-white' : 'btn-outline-violet'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-violet text-white' : 'btn-outline-violet'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <!-- UPDATED: Added flex-grow on mobile and Role restriction check -->
        <button v-if="canAddUser" type="button" class="btn btn-mint mb-0 flex-grow-1 flex-md-grow-0" @click="openAddModal">
          <i class="fas fa-user-plus me-2"></i> {{ $t('add_user') || 'Add User' }}
        </button>
      </div>
    </div>
    
    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- Inserted Search Bar Component -->
      <TableSearchLimit 
        :search="searchQuery" 
        @update:search="searchQuery = $event"
        :limit="itemsPerPage"
        @update:limit="itemsPerPage = $event"
        @refresh="getAllCandidate"
      />

      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ $t('name') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ $t('phone_number')
              }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ $t('status') }}
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">{{ $t('action') }}
              </th>

            </tr>
          </thead>
          <tbody>
            <tr v-for="(candidate, index) in paginatedCandidates" :key="index">
              <td>
                <div class="d-flex px-2 py-1">
                  <div class="d-flex flex-column justify-content-center">
                    <h6 class="mb-0 text-sm text-dark">{{ candidate.name }}</h6>
                  </div>
                </div>
              </td>
              <td>
                <p class="text-xs font-weight-bold mb-0 text-secondary">{{ candidate.phone }}</p>
              </td>
              <td>
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input custom-switch" 
                    type="checkbox" 
                    role="switch"
                    v-model="candidate.status"
                    true-value="1"
                    false-value="0"
                    @change="updateCandidateStatus(candidate.id, candidate.status)"
                  >
                </div>
              </td>
              <td class="align-middle text-center">
                <div class="d-flex justify-content-center align-items-center">
                  <!-- ADDED: View Candidate Button -->
                  <button class="btn btn-link text-dark p-0 mb-0 me-2" @click="viewCandidate(candidate)" title="View Details">
                    <i class="fas fa-eye text-xs text-info"></i>
                  </button>
                  <!-- UPDATED: Admin Reset Password Button with confirmation logic -->
                  <button v-if="canAddUser" class="btn btn-link text-dark p-0 mb-0 me-3" @click="adminResetPassword(candidate.id, candidate.name)" title="Reset Password">
                    <i class="fas fa-key text-xs text-primary"></i>
                  </button>
                  <TableActions 
                    @view="viewCandidate(candidate)"
                    @edit="() => navigateToEditCandidate(candidate)" 
                    @delete="confirmDelete(index, candidate.id)" 
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4">
        <div class="row">
          <div v-for="(candidate, index) in paginatedCandidates" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border-0 shadow-sm user-display-card">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center">
                    <div class="avatar avatar-sm bg-soft-violet me-2 rounded-circle d-flex align-items-center justify-content-center" style="width: 35px; height: 35px;">
                      <span class="text-violet fw-bold text-xs">{{ candidate.name?.charAt(0).toUpperCase() }}</span>
                    </div>
                    <div>
                      <h6 class="mb-0 text-sm text-dark">{{ candidate.name }}</h6>
                      <p class="text-xxs text-secondary mb-0">ID: {{ candidate.id }}</p>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <!-- ADDED: View Candidate Button for Card View -->
                    <button class="btn btn-link text-dark p-0 mb-0 me-2" @click="viewCandidate(candidate)" title="View Details">
                      <i class="fas fa-eye text-xs text-info"></i>
                    </button>
                    <!-- UPDATED: Admin Reset Password Button for Card View -->
                    <button v-if="canAddUser" class="btn btn-link text-dark p-0 mb-0 me-2" @click="adminResetPassword(candidate.id, candidate.name)" title="Reset Password">
                      <i class="fas fa-key text-xs text-primary"></i>
                    </button>
                    <TableActions 
                      @view="viewCandidate(candidate)"
                      @edit="() => navigateToEditCandidate(candidate)" 
                      @delete="confirmDelete(index, candidate.id)" 
                    />
                  </div>
                </div>
                
                <div class="mb-3">
                  <p class="text-xs mb-1 text-secondary"><strong><i class="fas fa-phone me-1 text-mint"></i> {{ $t('phone_number') }}:</strong> {{ candidate.phone }}</p>
                </div>

                <div class="d-flex justify-content-between align-items-center pt-2 border-top">
                  <span class="text-xs font-weight-bold text-dark">{{ $t('status') }}</span>
                  <div class="form-check form-switch mb-0">
                    <input 
                      class="form-check-input custom-switch" 
                      type="checkbox" 
                      role="switch"
                      v-model="candidate.status"
                      true-value="1"
                      false-value="0"
                      @change="updateCandidateStatus(candidate.id, candidate.status)"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Common Pagination and No Results logic -->
      <div v-if="filteredCandidates.length === 0" class="text-center py-4 text-secondary text-sm">
        No Users found.
      </div>

      <div class="pagination-container pb-3 flex-wrap">
        <button class="pagination-btn mb-2 mb-sm-0" @click="prevPage" :disabled="currentPage === 1">
          <i class="fas fa-chevron-left me-1"></i> {{ $t('previous') }}
        </button>
        <span class="text-xs font-weight-bold mx-3 text-secondary mb-2 mb-sm-0">Page {{ currentPage }} of {{ totalPages }}</span>
        <button class="pagination-btn" @click="nextPage" :disabled="currentPage === totalPages">
          {{ $t('next') }} <i class="fas fa-chevron-right ms-1"></i>
        </button>
      </div>
    </div>

    <!-- Add Modal -->
    <div v-if="showAddModal" class="custom-overlay" @click.self="showAddModal = false">
      <div class="modal-dialog modal-dialog-centered px-3" role="document">
        <div class="modal-content border-0 shadow-lg border-radius-xl overflow-hidden">
          <div class="modal-header bg-dark-teal py-3">
            <h5 class="modal-title text-white fw-bold">Add New User</h5>
            <button type="button" class="btn-close-custom" @click="showAddModal = false">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body p-3 p-md-4" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="addResource">
              <div class="form-group mb-3">
                <label class="text-xs fw-bold text-muted ps-1">{{ $t('user_name') || 'User Name' }}</label>
                <div class="input-group">
                  <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-user text-mint text-xs"></i></span>
                  <input v-model="newResource.name" type="text" class="form-control custom-input border-start-0" placeholder="John Doe" />
                </div>
                <small v-if="nameError" class="text-danger text-xxs">{{ $t('name_required') }}</small>
              </div>

              <!-- EMAIL ADDRESS FIELD -->
              <div class="form-group mb-3">
                <label class="text-xs fw-bold text-muted ps-1">Email Address</label>
                <div class="input-group">
                  <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-envelope text-mint text-xs"></i></span>
                  <input v-model="newResource.email" type="email" class="form-control custom-input border-start-0" placeholder="example@mail.com" />
                </div>
                <small v-if="emailError" class="text-danger text-xxs">Valid email is required</small>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">{{ $t('phone_number') }}</label>
                    <div class="input-group">
                      <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-phone text-mint text-xs"></i></span>
                      <input v-model="newResource.phone" type="text" class="form-control custom-input border-start-0" maxlength="10" placeholder="1234567890" @input="validatePhoneNumber" />
                    </div>
                    <small v-if="phoneError" class="text-danger text-xxs">{{ $t('phone_number_invalid') }}</small>
                  </div>
                </div>

                <!-- NEW PASSWORD FIELD -->
                <div class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">Password</label>
                    <div class="input-group">
                      <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-lock text-mint text-xs"></i></span>
                      <input v-model="newResource.password" type="password" class="form-control custom-input border-start-0" placeholder="Minimum 6 characters" required />
                    </div>
                    <small v-if="passwordError" class="text-danger text-xxs">Password is required (min 6 characters)</small>
                  </div>
                </div>

                <!-- START NEW INSTALLATION TYPE AND DEPARTMENT FIELDS -->
                <div class="col-md-12" v-if="isSuperAdmin">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">Installation Type</label>
                    <div class="input-group">
                      <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-tools text-mint text-xs"></i></span>
                      <select v-model="newResource.installation_type_id" class="form-control custom-input border-start-0" @change="onInstallationTypeChange" :required="isSuperAdmin">
                        <option value="" disabled>Select Type</option>
                        <option v-for="type in filteredInstallationTypes" :key="type.id" :value="type.id">
                          {{ type.name || type.installation_type_name || type.installation_name || type.type_name || ('Type #' + type.id) }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 col-6">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">Role</label>
                    <select v-model="newResource.role_id" class="form-control custom-input" required>
                      <option value="" disabled>Select Role</option>
                      <option v-for="role in filteredRoles" :key="role.id !== undefined ? role.id : role.role_id" :value="role.id !== undefined ? role.id : role.role_id">
                        {{ role.name || role.role_name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="col-md-6 col-6">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">{{ $t("status") }}</label>
                    <select v-model="newResource.status" class="form-control custom-input" required>
                      <option value="1">Active</option>
                      <option value="0">InActive</option>
                    </select>
                  </div>
                </div>

                <!-- CONDITIONAL GENDER FIELD: Displayed when selected role is Doctor -->
                <div v-if="showGenderField" class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="text-xs fw-bold text-muted ps-1">Gender</label>
                    <div class="input-group">
                      <span class="input-group-text bg-transparent border-end-0"><i class="fas fa-venus-mars text-mint text-xs"></i></span>
                      <select v-model="newResource.gender" class="form-control custom-input border-start-0" required>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                
                <!-- END NEW INSTALLATION TYPE AND DEPARTMENT FIELDS -->
              </div>

              <div class="text-center mt-4">
                <button type="submit" class="btn btn-mint text-white px-5 shadow-sm fw-bold w-100">
                  {{ $t('add_user') || 'Add User' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Swal from 'sweetalert2';
import axios from 'axios';
import '@fortawesome/fontawesome-free/css/all.css';
import CandidateDataService from "../../services/CandidateDataService.js";
import RolesDataService from "../../services/RolesDataService.js";
import TableSearchLimit from "@/components/TableSearchLimit.vue";
import TableActions from "@/components/TableActions.vue";

export default {
  name: "CandidatesTable",
  components: {
    TableSearchLimit,
    TableActions
  },
  data() {
    return {
      viewMode: 'card', 
      candidates: [],
      roles: [],
      installationTypes: [], // ADDED: New data property
      departments: [],      // ADDED: New data property
      mediRoles: [],        // ADDED: New data property for medical roles
      searchQuery: "", 
      newResource: {
        id: "",
        name: "",
        email: "", // Added email property
        phone: "",
        password: "", // Added
        status: '1',
        role_id: "",
        gender: "Male",
        installation_type_id: "", // ADDED: New property for installation
        dept_id: ""               // ADDED: New property for medical department
      },
      currentPage: 1,
      itemsPerPage: 20, 
      showAddModal: false,
      nameError: false,
      emailError: false, // Added email error state
      phoneError: false,
      passwordError: false, // Added
      selectedLanguage: this.$i18n.locale,
    };
  },
  created() {
    this.getAllCandidate();
    this.RolesDataService();
    this.fetchInstallationTypes();
  },
  mounted() {
    this.getAllCandidate();
  },
  activated() {
    this.getAllCandidate();
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    itemsPerPage() {
      this.currentPage = 1;
    }
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || localStorage.getItem('admin_role') || '').toLowerCase().trim();
      return ['superadmin', 'super admin', '1'].includes(role);
    },
    // UPDATED: Computed property to check if user has add permission
    canAddUser() {
      // SYNCED: checking both possible role keys
      const role = (localStorage.getItem('userRole') || localStorage.getItem('admin_role') || '').toLowerCase().replace(/\s+/g, '');
      return ['admin', 'supervisor', 'doctor', 'superadmin'].includes(role);
    },
    // Determine if selected role is Doctor
    isDoctorRole() {
      if (!this.newResource.role_id) return false;
      const selectedRoleObj = (this.filteredRoles || []).find(r => 
        String(r.id !== undefined ? r.id : r.role_id) === String(this.newResource.role_id)
      ) || (this.roles || []).find(r => 
        String(r.id !== undefined ? r.id : r.role_id) === String(this.newResource.role_id)
      );
      const roleName = selectedRoleObj ? (selectedRoleObj.name || selectedRoleObj.role_name || '') : '';
      return String(roleName).toLowerCase().includes('doctor');
    },
    // Conditionally show Gender field when selected role is Doctor
    showGenderField() {
      return this.isDoctorRole;
    },
    // ADDED: Check if current selection is Medical to toggle Dept field and Filter Roles
    isMedicalType() {
      const selected = this.installationTypes.find(t => t.id == this.newResource.installation_type_id);
      return selected && selected.name.toLowerCase().includes('medical');
    },
    // ADDED: Filter available installation types in modal based on logged in user's installation type
    filteredInstallationTypes() {
      const userInstType = (localStorage.getItem('installationType') || '').toLowerCase();
      const userInstId = localStorage.getItem('installation_type_id');

      if (userInstType === 'medical' || userInstId == '2' || userInstId == '5') {
        return (this.installationTypes || []).filter(t => t.id == 2 || t.id == 5 || String(t.name).toLowerCase().includes('medical'));
      } else if (userInstType === 'media' || userInstId == '1' || userInstId == '6') {
        return (this.installationTypes || []).filter(t => t.id == 1 || t.id == 6 || String(t.name).toLowerCase().includes('media'));
      }
      return this.installationTypes || [];
    },
    // Logic to show roles dynamically based on Installation Type and Backend Active Roles
    filteredRoles() {
      let targetInstId = this.newResource.installation_type_id;
      const loggedInInstType = (localStorage.getItem('installationType') || localStorage.getItem('installation_type') || '').toLowerCase();
      const loggedInInstId = String(localStorage.getItem('installation_type_id') || localStorage.getItem('installationType') || '');
      const allowedModulesStr = (localStorage.getItem('allowedModules') || '').toLowerCase();
      const userModulesStr = (localStorage.getItem('userModules') || '').toLowerCase();
      
      const isMedicalSession = loggedInInstType.includes('medical') || 
                               ['2', '5', '7', '27'].includes(loggedInInstId) || 
                               allowedModulesStr.includes('medical') || 
                               allowedModulesStr.includes('doctor') ||
                               userModulesStr.includes('medical') ||
                               userModulesStr.includes('doctor');

      if (!targetInstId && isMedicalSession) {
        targetInstId = loggedInInstId || 5;
      }

      const selected = (this.installationTypes || []).find(t => t.id == targetInstId);
      let typeName = selected && selected.name ? String(selected.name).toLowerCase() : String(targetInstId || loggedInInstType).toLowerCase();

      if (['2', '5', '7', '27'].includes(String(targetInstId)) || typeName.includes('medical') || isMedicalSession) {
        typeName = 'medical';
      } else if (['1', '6'].includes(String(targetInstId)) || typeName.includes('media')) {
        typeName = 'media';
      }

      // Filter out deleted/inactive roles from backend API list
      const activeRoles = (this.roles || []).filter(r => {
        if (!r) return false;
        const statusStr = String(r.status || '').toLowerCase();
        if (statusStr === 'inactive' || statusStr === '0' || statusStr === 'disabled' || r.deleted_at || r.is_deleted) {
          return false;
        }
        // Hide Super Admin role from Tenant Admin users in user creation dropdown
        if (!this.isSuperAdmin && (r.name?.toLowerCase() === 'superadmin' || r.name?.toLowerCase() === 'super admin' || r.type === 0)) {
          return false;
        }
        return true;
      });

      return activeRoles;
    },
    filteredCandidates() {
      if (!Array.isArray(this.candidates)) return [];
      if (!this.searchQuery) {
        return this.candidates;
      }
      const query = this.searchQuery.toLowerCase();
      return this.candidates.filter(candidate => 
        (candidate.name && candidate.name.toLowerCase().includes(query)) ||
        (candidate.phone && candidate.phone.toLowerCase().includes(query)) ||
        (candidate.email && candidate.email.toLowerCase().includes(query)) ||
        (candidate.id && String(candidate.id).toLowerCase().includes(query))
      );
    },
    totalPages() {
      return Math.ceil(this.filteredCandidates.length / this.itemsPerPage) || 1;
    },
    paginatedCandidates() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCandidates.slice(start, end);
    },
  },
  methods: {
    openAddModal() {
      this.RolesDataService();
      const userRole = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().trim();
      const isSuperAdmin = ['superadmin', 'super admin', '1'].includes(userRole);
      const userInstType = (localStorage.getItem('installationType') || localStorage.getItem('installation_type') || '').toLowerCase();
      const userInstId = localStorage.getItem('installation_type_id') || localStorage.getItem('installationType') || '';
      
      let targetId = "";
      if (userInstId) {
        targetId = userInstId;
      } else if (userInstType.includes('medical') || userInstId == '2' || userInstId == '5') {
        const medType = (this.installationTypes || []).find(t => t.id == 2 || t.id == 5 || String(t.name).toLowerCase().includes('medical'));
        targetId = medType ? medType.id : 5;
      } else if (userInstType.includes('media') || userInstId == '1' || userInstId == '6') {
        const mediaType = (this.installationTypes || []).find(t => t.id == 1 || t.id == 6 || String(t.name).toLowerCase().includes('media'));
        targetId = mediaType ? mediaType.id : 6;
      }

      this.newResource = {
        id: "",
        name: "",
        email: "",
        phone: "",
        password: "",
        status: '1',
        role_id: "",
        installation_type_id: isSuperAdmin ? "" : targetId,
        dept_id: ""
      };
      this.showAddModal = true;
    },
    onInstallationTypeChange() {
      this.newResource.role_id = "";
      if (this.newResource.installation_type_id) {
        this.RolesDataService(this.newResource.installation_type_id);
      }
    },
    // SYNCED: Method to get whatever token key is available
    getAuthToken() {
       return localStorage.getItem('token') || 
          localStorage.getItem('auth_token') || 
          localStorage.getItem('admin_token');
       
    },
    // ADDED: Fetch methods for the new dropdowns
async fetchInstallationTypes() {
  try {
    const token = this.getAuthToken();
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    // UPDATED: Revert to /v1/admin/ because /v1/mediadmin/ resulted in 404 earlier
    const response = await axios.get(`${apiBaseUrl}/v1/admin/installation-types`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    this.installationTypes = response.data.data || response.data;
  } catch (e) {
    console.error("Error fetching types", e);
  }
},
    fetchDepartments() {
      this.departments = [];
    },
    fetchMediRoles() {
      this.mediRoles = [];
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    validatePhoneNumber() {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(this.newResource.phone)) {
        this.phoneError = true;
      } else {
        this.phoneError = false;
      }
    },
    getAllCandidate() {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.VUE_APP_API_BASE_URL || '';
      const cleanBaseUrl = baseUrl.replace(/\/+$/, '');

      axios.get(`${cleanBaseUrl}/v1/admin/user?_t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }).then(response => {
          const res = response.data;
          if (Array.isArray(res)) {
            this.candidates = res;
          } else if (res && Array.isArray(res.data)) {
            this.candidates = res.data;
          } else if (res && Array.isArray(res.users)) {
            this.candidates = res.users;
          } else if (res && Array.isArray(res.candidates)) {
            this.candidates = res.candidates;
          } else if (res && Array.isArray(res.result)) {
            this.candidates = res.result;
          } else {
            this.candidates = [];
          }
          console.log("Fetched fresh users:", this.candidates);
        })
        .catch(e => {
          console.error("Error fetching users:", e);
        });
    },
    RolesDataService(instId = null) {
      const targetInstId = instId || (this.newResource && this.newResource.installation_type_id) || localStorage.getItem('installation_type_id') || localStorage.getItem('installationType') || '';
      const queryParam = this.isSuperAdmin && !instId 
        ? '' 
        : (targetInstId ? `?installation_type_id=${targetInstId}&include_common=1` : '');

      RolesDataService.getAll(queryParam).then(response => {
        this.roles = response.data.data || response.data;
      })
      .catch(e => {
        console.error("Error fetching roles:", e);
      });
    },
    navigateToAddCandidate() {
      this.$router.push('/mediauser-register');
    },
    navigateToEditCandidate(candidate) {
      if (!candidate || candidate instanceof Event || candidate.isTrusted || candidate.target) {
        console.error("Invalid candidate object passed:", candidate);
        return;
      }

      let candidateId = null;
      let candidateObj = {};

      if (typeof candidate === 'object' && candidate !== null) {
        candidateId = candidate.id || candidate.user_id || candidate.candidate_id;
        candidateObj = candidate;
      } else if (candidate !== undefined && candidate !== null) {
        candidateId = candidate;
      }

      if (!candidateId) {
        console.error("Candidate ID is missing:", candidate);
        return;
      }

      this.$router.push({
        path: `/edit-user/${candidateId}`,
        query: {
          email: candidateObj.email || '',
          name: candidateObj.name || '',
          phone: candidateObj.phone || '',
          role_id: candidateObj.role_id || candidateObj.roleId || '',
          gender: candidateObj.gender || '',
          status: candidateObj.status !== undefined ? candidateObj.status : '1',
          installation_type_id: candidateObj.installation_type_id || candidateObj.type || ''
        }
      });
    },
    // ADDED METHOD: View MediaUser details in a popup modal
    viewCandidate(candidate) {
      const roleObj = this.roles.find(r => r.id === candidate.role_id);
      const roleName = roleObj ? roleObj.name : (candidate.type || 'N/A');
      const genderLine = candidate.gender ? `<p class="mb-1"><strong>Gender:</strong> ${candidate.gender}</p>` : '';
      const statusBadge = candidate.status == 1 
        ? '<span class="badge bg-success">Active</span>' 
        : '<span class="badge bg-secondary">Inactive</span>';

      Swal.fire({
        title: `<strong>${candidate.name || 'User Details'}</strong>`,
        icon: 'info',
        html: `
          <div class="text-start px-2 py-2" style="font-size: 14px; line-height: 1.8;">
            <p class="mb-1"><strong>ID:</strong> ${candidate.id || 'N/A'}</p>
            <p class="mb-1"><strong>Name:</strong> ${candidate.name || 'N/A'}</p>
            <p class="mb-1"><strong>Email:</strong> ${candidate.email || 'N/A'}</p>
            <p class="mb-1"><strong>Phone:</strong> ${candidate.phone || 'N/A'}</p>
            <p class="mb-1"><strong>Role:</strong> ${roleName}</p>
            ${genderLine}
            <p class="mb-0"><strong>Status:</strong> ${statusBadge}</p>
          </div>
        `,
        confirmButtonText: 'Close',
        confirmButtonColor: '#1e3a5f'
      });
    },
    // --- UPDATED DELETE LOGIC ---
    confirmDelete(index, id) {
      console.log("Delete button clicked for ID:", id);
      
      Swal.fire({
        title: this.$t('are_you_sure'),
        text: "This user will be permanently removed.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          CandidateDataService.deleteAdmin(id)
            .then(() => {
              // Refresh the list after deletion
              this.getAllCandidate();
              Swal.fire(
                'Deleted!',
                'User has been deleted.',
                'success'
              );
            })
            .catch(error => {
              console.error(error);
              Swal.fire(
                'Error!',
                error.response?.data?.message || 'Failed to delete user.',
                'error'
              );
            });
        }
      });
    },
    // UPDATED METHOD: Admin can reset anyone's password with Password & Confirm Password
    adminResetPassword(userId, userName) {
      Swal.fire({
        title: `Reset Password`,
        html: `
          <p class="text-sm">Enter new password for <strong>${userName}</strong></p>
          <input type="password" id="admin-new-pass" class="swal2-input" placeholder="New Password">
          <input type="password" id="admin-confirm-pass" class="swal2-input" placeholder="Confirm Password">
        `,
        showCancelButton: true,
        confirmButtonText: 'Reset',
        confirmButtonColor: '#1e3a5f',
        focusConfirm: false,
        preConfirm: () => {
          const pass = document.getElementById('admin-new-pass').value;
          const confirm = document.getElementById('admin-confirm-pass').value;
          if (!pass || !confirm) {
            Swal.showValidationMessage('Please fill both fields');
            return false;
          }
          if (pass.length < 6) {
            Swal.showValidationMessage('Password must be at least 6 characters');
            return false;
          }
          if (pass !== confirm) {
            Swal.showValidationMessage('Passwords do not match');
            return false;
          }
          return pass;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          const token = this.getAuthToken();
          const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

          axios.post(`${apiBaseUrl}/v1/admin/user/change-password/${userId}`, 
            { password: result.value },
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(() => {
            Swal.fire('Updated!', 'User password has been reset successfully.', 'success');
          }).catch(err => {
            Swal.fire('Error', err.response?.data?.message || 'Failed to reset password.', 'error');
          });
        }
      });
    },
    addResource() {
      // UPDATED: Check permission before proceeding
      if (!this.canAddUser) {
        Swal.fire({ icon: 'error', title: 'Unauthorized', text: 'You do not have permission to add users.' });
        return;
      }

      // Name validation
      if (!this.newResource.name.trim()) {
        this.nameError = true;
      } else {
        this.nameError = false;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.newResource.email || !emailRegex.test(this.newResource.email)) {
        this.emailError = true;
      } else {
        this.emailError = false;
      }

      // Password validation
      if (!this.newResource.password || this.newResource.password.length < 6) {
        this.passwordError = true;
      } else {
        this.passwordError = false;
      }

      this.validatePhoneNumber();

      if (this.nameError || this.phoneError || this.passwordError || this.emailError) {
        return;
      }

      // Check if it's a medical user to find role from mediRoles
      const isMed = this.isMedicalType;
      const selectedRole = isMed 
          ? this.mediRoles.find(role => (role.role_id || role.id) == this.newResource.role_id)
          : this.roles.find(role => role.id == this.newResource.role_id);
      
      // Auto-assign installation_type_id for non-superadmin if missing
      if (!this.newResource.installation_type_id && !this.isSuperAdmin) {
        this.newResource.installation_type_id = localStorage.getItem('installation_type_id') || localStorage.getItem('installationType') || '';
      }

      const payload = {
        name: this.newResource.name,
        email: this.newResource.email, // Added email to payload
        password: this.newResource.password, // Added password to payload
        phone: this.newResource.phone.startsWith('+91')
          ? this.newResource.phone
          : '+91' + this.newResource.phone,
        status: this.newResource.status,
        role_id: this.newResource.role_id,
        installation_type_id: this.newResource.installation_type_id, // ADDED: New property
        dept_id: isMed ? this.newResource.dept_id : null, // ADDED: New property
        type: selectedRole?.type || (isMed ? 2 : undefined), // Fallback for medical roles
        gender: this.showGenderField ? (this.newResource.gender || 'Male') : null
      };

      CandidateDataService.create(payload)
        .then(response => {
          this.showAddModal = false;
          // Use response data from backend
          if (response.data.user) {
             this.candidates.push(response.data.user); 
          } else {
             this.getAllCandidate(); // Refresh if response structure differs
          }
          this.resetNewResource();
          Swal.fire({ icon: 'success', title: 'Success', text: 'New User Added successfully!' });
        })
        .catch(error => { 
          console.error(error); 
          Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to add user' });
        });
    },
    async updateCandidateStatus(candidateId, newStatus) {
      try {
        await CandidateDataService.updateStatus(candidateId, { status: newStatus });
        Swal.fire(this.$t('success'), this.$t('mediauser_status_updated'), 'success');
      } catch (error) {
        const candidate = this.candidates.find(c => c.id === candidateId);
        if (candidate) { candidate.status = candidate.status === 1 ? 0 : 1; }
        Swal.fire(this.$t('error'), this.$t('mediauser_status_update_error'), 'error');
      }
    },
    resetNewResource() {
      this.newResource = { 
        id: "", 
        name: "", 
        email: "", 
        phone: "", 
        password: "", 
        status: '1', 
        role_id: "", 
        gender: "Male",
        installation_type_id: "", 
        dept_id: "" 
      };
      this.nameError = false;
      this.emailError = false;
      this.phoneError = false;
      this.passwordError = false;
    },
    prevPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
  },
};
</script>

<style scoped>
/* Styles preserved exactly as provided in the original file */
.text-mint, .text-violet, .fa-phone, .fa-user, .fa-lock, .fa-envelope, .fa-tools, .fa-hospital { 
  color: var(--bs-primary) !important; 
}

.btn-mint { 
  background-color: var(--bs-primary) !important; 
  border: none; 
  color: white !important; 
  transition: all 0.25s ease; 
  font-weight: 800 !important; 
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.btn-mint:hover { 
  filter: brightness(0.9);
  transform: translateY(-1px); 
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.btn-violet { 
  background-color: var(--bs-primary) !important; 
  border: 1px solid var(--bs-primary) !important; 
  color: white !important;
}

.btn-outline-violet { 
  border: 1px solid var(--bs-primary) !important; 
  color: var(--bs-primary) !important; 
  background: transparent; 
  font-weight: 600;
}
.btn-outline-violet:hover {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.bg-dark-teal { 
  background-color: var(--bs-primary) !important; 
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1.5rem;
  padding-bottom: 1rem;
}

.pagination-btn {
  background-color: var(--bs-primary) !important; 
  color: #ffffff !important;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 12px;
  font-weight: 800 !important; 
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.pagination-btn:disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
  box-shadow: none;
}

.pagination-btn:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

.custom-switch:checked {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.custom-input:focus {
  border-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.15) !important;
}

.bg-soft-violet {
  background-color: rgba(0, 0, 0, 0.05) !important; 
  border: 1px solid var(--bs-primary) !important;
}

.user-display-card {
  transition: all 0.25s ease;
  background: #ffffff;
  border: 1px solid #edf2f7 !important;
  border-radius: 12px;
}
.user-display-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 20px -10px rgba(0, 0, 0, 0.15) !important;
}

.custom-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px);
  z-index: 1050; display: flex; justify-content: center; align-items: center;
}

/* --- UPDATED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  /* Removed width:100% to let it share row space better or stack via flex-wrap */
  .btn-mint {
    font-size: 11px;
    padding: 8px 12px;
  }
  .btn-violet, .btn-outline-violet {
    padding: 8px 12px;
  }
  .pagination-btn {
    padding: 8px 12px;
    font-size: 11px;
  }
  .card-header {
    padding-bottom: 15px !important;
  }
  .modal-dialog {
    margin: 0.5rem;
  }
}

/* Fix for very narrow screens to prevent text overlap */
@media (max-width: 400px) {
  .btn-mint {
    width: 100%;
    margin-top: 5px;
  }
  .btn-group {
    width: 100%;
    display: flex;
  }
  .btn-group .btn {
    flex: 1;
  }
}
</style>